/**
 * Utility animation-utility
 * @description Homepage animations.
 * @copyright 2019-8-8
 * @file
 */

/**
 * Primary animation class name.
 */
const animatedClass = 'hp-animated';

/**
 * @description Adds class callback fn
 * @method addClass
 * @param {*} classes Mixed types, CSS classes string or an array of strings.
 * @returns {void}
 */
function addClass(classes) {
  if (classes instanceof Array) {
    classes.map(cls => this.element.classList.add(cls));
  } else if (typeof classes === 'string') {
    this.element.classList.add(classes);
  }
}

/**
 * @description Remove class callback fn
 * @method removeClass
 * @param {*} classes Mixed types, CSS classes string or an array of strings.
 * @returns {void}
 */
function removeClass(classes) {
  if (classes instanceof Array) {
    classes.map(cls => this.element.classList.remove(cls));
  } else if (typeof classes === 'string') {
    this.element.classList.remove(classes);
  }
}

/**
 * Available callback functions.
 */
const callbackFunctions = {
  addClass,
  removeClass
};

/**
 * Cross-browser animation end events.
 */
const animationEndEvents = ['animationend', 'webkitAnimationEnd', 'oAnimationEnd'];

/**
 * @description Animate DOM Element with animation class name and a callback function.
 * @method animate
 * @param {*} element Mixed types, String or HTMLElement.
 * @param {*} animationName Mixed types, String or Array.
 * @param {*} callback Mixed types, Function or Array.
 * @returns {void}
 */
function animate(element, animationName, callback) {
  // Element is a string
  if (typeof element === 'string') {
    const nodes = document.querySelectorAll(element);
    [...nodes].map(node => animate(node, animationName, callback));
  }

  // Element is an object
  if (typeof element === 'object') {
    /**
     * @method handleAnimationEnd
     * @returns {void}
     */
    function handleAnimationEnd() {
      if (typeof callback === 'function') callback();
      if (typeof callback === 'object') {
        if (callback instanceof Array) {
          callback.map(cb => {
            if (cb.fn) {
              callbackFunctions[cb.fn].apply({
                element
              }, cb.args);
            }
          });
        } else if (callback.fn) {
          callbackFunctions[callback.fn].apply({
            element
          }, callback.args);
        }
      }
      element.classList.remove(animatedClass);
      if (typeof animationName === 'string') {
        element.classList.remove(animationName);
      } else if (animationName.length) {
        // IE requires individual argument.
        animationName.map(aName => element.classList.remove(aName));
      }
      animationEndEvents.map(endEvent => element.removeEventListener(endEvent, handleAnimationEnd));
    }
    element.classList.add(animatedClass);
    if (typeof animationName === 'string') {
      element.classList.add(animationName);
    } else if (animationName.length) {
      // IE requires individual argument.
      animationName.map(aName => element.classList.add(aName));
    }
    animationEndEvents.map(endEvent => element.addEventListener(endEvent, handleAnimationEnd, callback));
  }
}
/**
 * @description Initialize animation instructions from JSON parsed data.
 * @method init
 * @example JCR structure above docblock.
 * @param {HTMLElement} scope Parent node of the child nodes to be animated.
 * @param {Object} rules JSON parsed animation configuration, comes from JCR - animations property.
 * @returns {void}
 */
function init(scope, rules) {
  rules.forEach(rule => {
    // If no selector is passed, use scope as trigger
    if (rule.trigger && rule.trigger.selector) {
      const triggers = scope.querySelectorAll(rule.trigger.selector);
      [...triggers].forEach(trigger => {
        rule.trigger.events.forEach(event => {
          trigger.addEventListener(event, () => {
            // Get targets
            if (rule.target.selector && rule.target.selector !== rule.trigger.selector) {
              const targets = trigger.querySelectorAll(rule.target.selector);
              [...targets].map(target => animate(target, rule.target.animation, rule.callbacks));
            } else if (rule.target.selector && rule.target.selector === rule.trigger.selector) {
              animate(trigger, rule.target.animation, rule.callbacks);
            } else {
              animate(scope, rule.target.animation, rule.callbacks);
            }
          });
        });
      });
    } else if (rule.trigger && rule.trigger.events) {
      rule.trigger.events.forEach(event => {
        scope.addEventListener(event, () => {
          // Get targets
          if (rule.target.selector && rule.target.selector !== rule.trigger.selector) {
            const targets = scope.querySelectorAll(rule.target.selector);
            [...targets].map(target => animate(target, rule.target.animation, rule.callbacks));
          } else if (rule.target.selector && rule.target.selector === rule.trigger.selector) {
            animate(rule.trigger, rule.target.animation, rule.callbacks);
          } else {
            animate(scope, rule.target.animation, rule.callbacks);
          }
        });
      });
    } else if (rule.target) {
      // No trigger
      if (rule.target.selector) {
        const targets = scope.querySelectorAll(rule.target.selector);
        [...targets].map(target => animate(target, rule.target.animation, rule.callbacks));
      } else {
        animate(scope, rule.target.animation, rule.callbacks);
      }
    }
  });
}

/**
 * @description Export functions
 * @param {object=} functions
 * @export
 */
var index = {
  animate,
  init
};

export { index as default };
